import type {
  CharityCheckWire,
  CharityDisputeWire,
  CharityDocumentWire,
  CharityExtractionWire,
  CharityVerdictWire,
} from '../api/adminOps';
import { StatusChip } from '../components/ui';
import {
  checkResultTone,
  extractedFieldRows,
  formatBytes,
  formatConfidence,
  isViewLinkExpired,
  prettyJson,
  sampleReviewChip,
  sampleReviewState,
  severityChip,
  verdictChip,
} from './charityVerification.logic';
import { Empty, Field, SECTION_HEADING, TextValue } from './CharityVerificationCaseView';
import { formatWhen } from './userDeletion.logic';

export const VIEW_LINK_TEXT = 'View document (link valid 60 s)';
export const LINK_EXPIRED_TEXT = 'Link expired';
export const REFETCH_TEXT = 'Re-fetch';

interface DocumentsProps {
  documents: CharityDocumentWire[];
  /** The render time, so an expired link offers a re-fetch instead of a 403. */
  now: number;
  /** Asks for the claim again, which re-signs every document link. */
  onRefetch: () => void;
}

function DocumentLink({ doc, now, onRefetch }: DocumentsProps & { doc: CharityDocumentWire }) {
  if (doc.view_url === null) {
    return <span className="text-xs text-gray-text">No file to view</span>;
  }
  if (isViewLinkExpired(doc.view_url_expires_at, now)) {
    return (
      <span className="flex items-center gap-2 text-xs">
        <span className="text-yellow-400">{LINK_EXPIRED_TEXT}</span>
        <button type="button" className="btn-secondary text-xs px-3 py-1" onClick={onRefetch}>
          {REFETCH_TEXT}
        </button>
      </span>
    );
  }
  return (
    <a
      href={doc.view_url}
      target="_blank"
      rel="noreferrer"
      className="text-xs text-primary underline"
    >
      {VIEW_LINK_TEXT}
    </a>
  );
}

function DocumentsPanel(props: DocumentsProps) {
  return (
    <section aria-labelledby="charity-documents">
      <h3 id="charity-documents" className={SECTION_HEADING}>
        Documents
      </h3>
      {props.documents.length === 0 ? (
        <p className="text-sm text-gray-text">No document has been uploaded.</p>
      ) : (
        <ul className="space-y-2">
          {props.documents.map(document => (
            <li key={document.id} className="rounded border border-gray-border p-3 space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-white break-all">{document.filename}</span>
                <StatusChip label={document.status} tone="neutral" />
              </div>
              <p className="text-xs text-gray-text">
                {document.slot} · {document.content_type} · {formatBytes(document.size_bytes)} ·
                uploaded {formatWhen(document.uploaded_at, 'never')}
                {document.letter_kind !== null && ` · ${document.letter_kind}`}
              </p>
              {document.bounce_reason !== null && (
                <p className="text-xs text-yellow-400">Bounced: {document.bounce_reason}</p>
              )}
              {document.rejection_reason !== null && (
                <p className="text-xs text-red-400">Rejected: {document.rejection_reason}</p>
              )}
              <DocumentLink {...props} doc={document} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ExtractionsPanel({ extractions }: { extractions: CharityExtractionWire[] }) {
  return (
    <section aria-labelledby="charity-extractions">
      <h3 id="charity-extractions" className={SECTION_HEADING}>
        Letter extraction
      </h3>
      {extractions.length === 0 ? (
        <p className="text-sm text-gray-text">No letter has been read yet.</p>
      ) : (
        <ul className="space-y-3">
          {extractions.map(extraction => (
            <li
              key={`${extraction.document_id}-${extraction.extracted_at}`}
              className="rounded border border-gray-border p-3 space-y-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip label={extraction.provider} tone="info" />
                {extraction.fallback_used && <StatusChip label="Fallback provider" tone="warn" />}
                <span className="text-xs text-gray-text">
                  {extraction.model ?? 'model not recorded'} · document{' '}
                  <span className="font-mono">{extraction.document_id}</span> ·{' '}
                  {formatWhen(extraction.extracted_at)}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                <Field label="Letter kind">
                  <TextValue value={extraction.letter_kind} />
                </Field>
                <Field label="Letter confidence">
                  {formatConfidence(extraction.letter_confidence)}
                </Field>
              </dl>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-text">
                    <th scope="col" className="py-1 font-medium">
                      Field
                    </th>
                    <th scope="col" className="py-1 font-medium">
                      Extracted
                    </th>
                    <th scope="col" className="py-1 font-medium">
                      Confidence
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {extractedFieldRows(extraction).map(row => (
                    <tr key={row.label} className="border-t border-gray-border/50">
                      <th scope="row" className="py-1 pr-2 text-left font-medium text-gray-text">
                        {row.label}
                      </th>
                      <td className="py-1 pr-2 text-white break-all">
                        <TextValue value={row.value} />
                      </td>
                      <td className="py-1 text-white tabular-nums">
                        {formatConfidence(row.confidence)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ChecksTable({ checks }: { checks: CharityCheckWire[] }) {
  if (checks.length === 0) {
    return <p className="text-sm text-gray-text">No automated check has run.</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-text">
          <th scope="col" className="py-1 pr-2 font-medium">
            Check
          </th>
          <th scope="col" className="py-1 pr-2 font-medium">
            Result
          </th>
          <th scope="col" className="py-1 pr-2 font-medium">
            Severity
          </th>
          <th scope="col" className="py-1 font-medium">
            Detail
          </th>
        </tr>
      </thead>
      <tbody>
        {checks.map(check => {
          const severity = severityChip(check.severity);
          return (
            <tr
              key={`${check.check_type}-${check.document_id ?? ''}-${check.checked_at}`}
              className="border-t border-gray-border/50 align-top"
            >
              <th scope="row" className="py-1 pr-2 text-left font-medium text-white">
                {check.check_type}
                <span className="block text-gray-text font-normal">
                  {formatWhen(check.checked_at)}
                  {check.document_id !== null && (
                    <>
                      {' · '}
                      <span className="font-mono">{check.document_id}</span>
                    </>
                  )}
                </span>
              </th>
              <td className="py-1 pr-2">
                <StatusChip label={check.result} tone={checkResultTone(check.result)} />
              </td>
              <td className="py-1 pr-2">
                <StatusChip label={severity.label} tone={severity.tone} />
              </td>
              <td className="py-1">
                <pre className="whitespace-pre-wrap break-words rounded bg-dark-lighter p-2 font-mono text-gray-text">
                  {prettyJson(check.detail)}
                </pre>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function VerdictsList({ verdicts }: { verdicts: CharityVerdictWire[] }) {
  if (verdicts.length === 0) {
    return <p className="text-sm text-gray-text">No automated verdict has been recorded.</p>;
  }
  return (
    <ul className="space-y-2">
      {verdicts.map(verdict => {
        const chip = verdictChip(verdict.verdict, verdict.mode);
        const severity = severityChip(verdict.highest_severity);
        const sampleState = sampleReviewState(verdict, new Date());
        return (
          <li
            key={verdict.run_id}
            className="rounded border border-gray-border p-3 space-y-1 text-xs"
          >
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip label={chip.label} tone={chip.tone} />
              <StatusChip label={severity.label} tone={severity.tone} />
              {verdict.applied && <StatusChip label="Applied" tone="ok" />}
              {verdict.sampled_for_review && <StatusChip label="Sampled" tone="neutral" />}
              {sampleState !== null && (
                <StatusChip
                  label={sampleReviewChip(sampleState).label}
                  tone={sampleReviewChip(sampleState).tone}
                />
              )}
              <span className="text-gray-text">
                {formatWhen(verdict.decided_at)} · run{' '}
                <span className="font-mono">{verdict.run_id}</span>
              </span>
            </div>
            {verdict.reasons.length > 0 && (
              <ul className="list-disc pl-4 text-gray-text">
                {verdict.reasons.map(reason => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
            <p className="text-gray-text">
              Reviewer agreement: {verdict.reviewer_agreement ?? 'not applicable'}
              {verdict.sample_due_at !== null &&
                ` · sample due ${formatWhen(verdict.sample_due_at)}`}
            </p>
            {verdict.rubric !== null && verdict.rubric !== undefined && (
              <pre className="whitespace-pre-wrap break-words rounded bg-dark-lighter p-2 font-mono text-gray-text">
                {prettyJson(verdict.rubric)}
              </pre>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function AutomationPanel({
  checks,
  verdicts,
}: {
  checks: CharityCheckWire[];
  verdicts: CharityVerdictWire[];
}) {
  return (
    <section aria-labelledby="charity-automation" className="space-y-3">
      <h3 id="charity-automation" className={SECTION_HEADING}>
        Automation
      </h3>
      <ChecksTable checks={checks} />
      <VerdictsList verdicts={verdicts} />
    </section>
  );
}

function DisputesPanel({ disputes }: { disputes: CharityDisputeWire[] }) {
  return (
    <section aria-labelledby="charity-disputes">
      <h3 id="charity-disputes" className={SECTION_HEADING}>
        Disputes
      </h3>
      {disputes.length === 0 ? (
        <p className="text-sm text-gray-text">Nobody has reported this claim.</p>
      ) : (
        <ul className="space-y-2 text-xs">
          {disputes.map(dispute => (
            <li key={dispute.id} className="rounded border border-red-500/30 p-3 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip label={dispute.stage} tone="neutral" />
                <StatusChip label={dispute.status} tone="crit" />
                <span className="text-gray-text">
                  {formatWhen(dispute.created_at)}
                  {dispute.resolved_at !== null && ` · resolved ${formatWhen(dispute.resolved_at)}`}
                </span>
              </div>
              <p className="text-gray-text whitespace-pre-wrap">
                {dispute.details === null || dispute.details === '' ? <Empty /> : dispute.details}
              </p>
              {(dispute.resolved_by ?? null) !== null && (
                <p className="text-gray-text">
                  Resolved by <span className="font-mono">{dispute.resolved_by}</span>
                  {dispute.resolution_note ? `: ${dispute.resolution_note}` : ''}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export interface CharityVerificationEvidenceProps extends DocumentsProps {
  extractions: CharityExtractionWire[];
  checks: CharityCheckWire[];
  verdicts: CharityVerdictWire[];
  disputes: CharityDisputeWire[];
}

/** DM-7976: the evidence behind a claim — documents, what was read from them, and what automation made of it. */
export default function CharityVerificationEvidence({
  documents,
  now,
  onRefetch,
  extractions,
  checks,
  verdicts,
  disputes,
}: CharityVerificationEvidenceProps) {
  return (
    <div className="space-y-5">
      <DocumentsPanel documents={documents} now={now} onRefetch={onRefetch} />
      <ExtractionsPanel extractions={extractions} />
      <AutomationPanel checks={checks} verdicts={verdicts} />
      <DisputesPanel disputes={disputes} />
    </div>
  );
}
